import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '@/context/AppContext';
import { PageHeader, Card } from '@/components/common';
import { formatCurrency } from '@/lib/format';
import { toast } from 'sonner';
import { Lightbulb, TrendingUp, Check, X, Sparkles } from 'lucide-react';

const stages = ['detected', 'analyzed', 'presented', 'accepted', 'dismissed'];
const stageColor = { detected: 'bg-zinc-100 text-zinc-500', analyzed: 'bg-blue-100 text-blue-600', presented: 'bg-violet-100 text-violet-600', accepted: 'bg-emerald-100 text-emerald-600', dismissed: 'bg-zinc-200 text-zinc-500' };

const Recommendations = () => {
  const { t, tx, lang, data } = useApp();
  const [recs, setRecs] = useState(data.recommendations);

  const act = (id, stage) => {
    setRecs((r) => r.map((x) => x.id === id ? { ...x, stage } : x));
    toast.success(stage === 'accepted' ? tx({ es: 'Recomendación aceptada', en: 'Recommendation accepted' }) : tx({ es: 'Recomendación descartada', en: 'Recommendation dismissed' }));
  };

  return (
    <div>
      <PageHeader title={t('rec.title')} subtitle={t('rec.subtitle')} />

      {/* Lifecycle legend */}
      <div className="flex flex-wrap items-center gap-2 mb-6 text-xs">
        {stages.map((s, i) => (
          <React.Fragment key={s}>
            <span className={`px-2.5 py-1 rounded-full font-medium ${stageColor[s]}`}>{t(`rec.${s}`)}</span>
            {i < stages.length - 1 && <span className="text-zinc-300">→</span>}
          </React.Fragment>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {recs.map((r, i) => {
          const closed = r.stage === 'accepted' || r.stage === 'dismissed';
          return (
            <motion.div key={r.id} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
              <Card className="p-5 h-full flex flex-col" hover data-testid={`rec-${r.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-cyan-100 to-violet-100 flex items-center justify-center"><Lightbulb className="w-4.5 h-4.5 text-violet-600" /></div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${stageColor[r.stage]}`} data-testid={`rec-stage-${r.id}`}>{t(`rec.${r.stage}`)}</span>
                </div>
                <h3 className="font-heading font-semibold text-zinc-900 leading-snug mb-2">{tx(r.title)}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed flex-1">{tx(r.desc)}</p>
                <div className="mt-4 pt-4 border-t border-zinc-100">
                  <div className="flex items-center gap-1.5 mb-3">
                    <TrendingUp className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs text-zinc-400">{t('rec.impact')}:</span>
                    <span className="font-mono-num font-bold text-emerald-600 text-sm">{formatCurrency(r.impact, lang)}</span>
                  </div>
                  {!closed && (
                    <div className="flex gap-2">
                      <button onClick={() => act(r.id, 'accepted')} data-testid={`rec-accept-${r.id}`} className="flex-1 h-9 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors">
                        <Check className="w-3.5 h-3.5" /> {t('common.accept')}
                      </button>
                      <button onClick={() => act(r.id, 'dismissed')} data-testid={`rec-dismiss-${r.id}`} className="flex-1 h-9 rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-50 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors">
                        <X className="w-3.5 h-3.5" /> {t('common.dismiss')}
                      </button>
                    </div>
                  )}
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Recommendations;
